import { useEffect, useRef, useState } from "react";
import { LOCALES } from "./config";
import { useI18n } from "./I18nProvider";
import { cn } from "../ui/cn";

/**
 * LanguageSelector — compact language switcher for TopNavigation, placed
 * immediately next to the NetworkBadge ("Base Sepolia"). Native to THE GRID
 * visual language (neon-glass pill). Shows "EN ▾"; the dropdown lists native
 * language names. Selection is applied instantly and persisted (localStorage).
 */
export function LanguageSelector() {
  const { locale, setLocale, t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = LOCALES.find((l) => l.code === locale) ?? LOCALES[0];

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative shrink-0" data-testid="language-selector">
      <button
        type="button"
        data-testid="language-selector-btn"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("lang.ariaLabel")}
        className={cn(
          "inline-flex items-center gap-1.5 h-7 px-2 2xl:px-2.5",
          "font-mono text-[10px] font-medium tracking-[0.16em] uppercase",
          "text-text-secondary hover:text-text-primary",
          "neon-glass pill-ghost",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)]",
        )}
      >
        <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden>
          <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.1" />
          <path d="M1.5 7h11M7 1.5c1.7 1.6 1.7 9.4 0 11M7 1.5c-1.7 1.6-1.7 9.4 0 11" stroke="currentColor" strokeWidth="1.1" />
        </svg>
        <span data-testid="language-current">{current.short}</span>
        <svg
          width="8"
          height="8"
          viewBox="0 0 10 10"
          fill="none"
          aria-hidden
          className={cn("transition-transform duration-fast", open && "rotate-180")}
        >
          <path d="M2 3.5 5 6.5l3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          data-testid="language-selector-menu"
          aria-label={t("lang.menuLabel")}
          className={cn(
            "absolute right-0 top-[calc(100%+6px)] z-modal",
            "min-w-[190px] rounded-md",
            "bg-panel border border-border-soft shadow-panel",
            "p-1",
          )}
        >
          {LOCALES.map((l) => {
            const active = l.code === locale;
            return (
              <button
                key={l.code}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                data-testid={`language-option-${l.code}`}
                data-active={active ? "true" : "false"}
                onClick={() => {
                  setLocale(l.code);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center justify-between gap-3 px-3 py-2 rounded-sm text-left",
                  "text-[12.5px]",
                  active
                    ? "text-neon-teal bg-elevated"
                    : "text-text-primary hover:bg-elevated",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)]",
                )}
              >
                <span>{l.label}</span>
                <span className="font-mono text-[10px] tracking-[0.14em] text-text-muted">
                  {l.short}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
